import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { employerAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  BarChart3, TrendingUp, Users, Star, Calendar, AlertTriangle, 
  Loader2, Trophy, Target, CheckCircle, Clock, User
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  PieChart, Pie, Cell,
  BarChart, Bar,
  Legend
} from 'recharts';

// Custom tooltip style
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-lg p-3 shadow-lg">
        <p className="text-[#0A0F1E] font-medium">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Custom chart axis styling
const chartAxisStyle = {
  fill: '#5B6472',
  fontSize: 12,
};

const PerformanceAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30d');
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employerAPI.getPerformanceAnalytics(dateRange);
      setData(res.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const dateRangeLabels = {
    '30d': 'Last 30 Days',
    '90d': 'Last 90 Days',
    '12m': 'Last 12 Months',
    'all': 'All Time'
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="performance-analytics">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E] flex items-center gap-2">
              <BarChart3 className="w-8 h-8 text-[#1B5AFF]" />
              Performance Analytics
            </h1>
            <p className="mt-2 text-[#5B6472]">Insights and trends across your team</p>
          </div>
          
          {/* Date Range Filter */}
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[180px] bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] rounded-xl" data-testid="date-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#FFFFFF] border-[#E8EDF5]">
              <SelectItem value="30d" className="text-[#0A0F1E]">Last 30 Days</SelectItem>
              <SelectItem value="90d" className="text-[#0A0F1E]">Last 90 Days</SelectItem>
              <SelectItem value="12m" className="text-[#0A0F1E]">Last 12 Months</SelectItem>
              <SelectItem value="all" className="text-[#0A0F1E]">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Summary Stats */}
        {data?.summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in">
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#5B6472] text-xs sm:text-sm">Total Records</p>
                  <p className="text-2xl sm:text-3xl font-bold text-[#0A0F1E] mt-1">{data.summary.totalRecords}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
              </div>
            </Card>
            
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#5B6472] text-xs sm:text-sm">Avg Team Rating</p>
                  <p className="text-2xl sm:text-3xl font-bold text-[#0A0F1E] mt-1">{data.summary.avgTeamRating}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-400" />
                </div>
              </div>
            </Card>
            
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#5B6472] text-xs sm:text-sm">Completion Rate</p>
                  <p className="text-2xl sm:text-3xl font-bold text-[#0A0F1E] mt-1">{data.summary.completionRate}%</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
              </div>
            </Card>
            
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4 sm:p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#5B6472] text-xs sm:text-sm">Overdue Reviews</p>
                  <p className="text-2xl sm:text-3xl font-bold text-[#0A0F1E] mt-1">{data.summary.overdueReviews}</p>
                </div>
                <div className={`w-10 h-10 rounded-xl ${data.summary.overdueReviews > 0 ? 'bg-red-500/20' : 'bg-[#F5F8FF]'} flex items-center justify-center`}>
                  <AlertTriangle className={`w-5 h-5 ${data.summary.overdueReviews > 0 ? 'text-red-400' : 'text-[#5B6472]'}`} />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Charts Row 1 */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Team Performance Over Time (Line Chart) */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="w-5 h-5 text-[#1B5AFF]" />
              <h3 className="text-lg font-semibold text-[#0A0F1E]">Team Performance Over Time</h3>
            </div>
            {data?.teamPerformanceOverTime?.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.teamPerformanceOverTime}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF5" />
                  <XAxis dataKey="month" tick={chartAxisStyle} axisLine={{ stroke: '#E8EDF5' }} />
                  <YAxis domain={[0, 5]} tick={chartAxisStyle} axisLine={{ stroke: '#E8EDF5' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="avgRating" 
                    name="Avg Rating"
                    stroke="#1B5AFF" 
                    strokeWidth={3}
                    dot={{ fill: '#1B5AFF', strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: '#1B5AFF' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-[#5B6472]">
                <p>No performance data available for this period</p>
              </div>
            )}
          </Card>

          {/* Rating Breakdown (Radar Chart) */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-6">
              <Target className="w-5 h-5 text-[#1B5AFF]" />
              <h3 className="text-lg font-semibold text-[#0A0F1E]">Rating Breakdown</h3>
            </div>
            {data?.ratingBreakdown?.some(r => r.score > 0) ? (
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={data.ratingBreakdown}>
                  <PolarGrid stroke="#E8EDF5" />
                  <PolarAngleAxis dataKey="category" tick={{ fill: '#5B6472', fontSize: 12 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 5]} tick={{ fill: '#5B6472', fontSize: 10 }} />
                  <Radar 
                    name="Score" 
                    dataKey="score" 
                    stroke="#1B5AFF" 
                    fill="#1B5AFF" 
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                  <Tooltip content={<CustomTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-[#5B6472]">
                <p>No rating data available</p>
              </div>
            )}
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Review Completion (Donut Chart) */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-6">
              <CheckCircle className="w-5 h-5 text-[#1B5AFF]" />
              <h3 className="text-lg font-semibold text-[#0A0F1E]">Review Status</h3>
            </div>
            {data?.reviewCompletion?.some(r => r.value > 0) ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={data.reviewCompletion}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                    labelLine={{ stroke: '#5B6472' }}
                  >
                    {data.reviewCompletion.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-[#5B6472]">
                <p>No scheduled reviews</p>
              </div>
            )}
          </Card>

          {/* Skill Frequency (Bar Chart) */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in lg:col-span-2">
            <div className="flex items-center gap-2 mb-6">
              <BarChart3 className="w-5 h-5 text-[#1B5AFF]" />
              <h3 className="text-lg font-semibold text-[#0A0F1E]">Top Skills Across Team</h3>
            </div>
            {data?.skillFrequency?.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.skillFrequency} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF5" />
                  <XAxis type="number" tick={chartAxisStyle} axisLine={{ stroke: '#E8EDF5' }} />
                  <YAxis dataKey="skill" type="category" width={100} tick={chartAxisStyle} axisLine={{ stroke: '#E8EDF5' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Count" fill="#1B5AFF" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-[#5B6472]">
                <p>No skill data available</p>
              </div>
            )}
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Rehire Eligibility Breakdown */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-6">
              <Users className="w-5 h-5 text-[#1B5AFF]" />
              <h3 className="text-lg font-semibold text-[#0A0F1E]">Rehire Eligibility</h3>
            </div>
            {data?.rehireBreakdown?.some(r => r.count > 0) ? (
              <div className="space-y-4">
                {data.rehireBreakdown.map((item, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[#0A0F1E] text-sm">{item.status}</span>
                      <span className="text-[#5B6472] text-sm">{item.count} ({item.percentage}%)</span>
                    </div>
                    <div className="w-full bg-[#F5F8FF] rounded-full h-3">
                      <div 
                        className="h-3 rounded-full transition-all duration-500"
                        style={{ width: `${item.percentage}%`, backgroundColor: item.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-[#5B6472]">
                <p>No rehire data available</p>
              </div>
            )}
          </Card>

          {/* Top Performers */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 animate-fade-in">
            <div className="flex items-center gap-2 mb-6">
              <Trophy className="w-5 h-5 text-[#1B5AFF]" />
              <h3 className="text-lg font-semibold text-[#0A0F1E]">Top Performers</h3>
            </div>
            {data?.topPerformers?.length > 0 ? (
              <div className="space-y-3">
                {data.topPerformers.map((performer, index) => (
                  <div 
                    key={performer.id} 
                    className="flex items-center gap-4 p-3 rounded-xl bg-[#F5F8FF]/50 border border-[#E8EDF5]"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-amber-500/20 text-amber-400' :
                      index === 1 ? 'bg-[#5B6472]/20 text-[#5B6472]' :
                      index === 2 ? 'bg-amber-700/20 text-amber-600' :
                      'bg-[#F5F8FF] text-[#5B6472]'
                    }`}>
                      {index + 1}
                    </div>
                    {performer.photo ? (
                      <img src={performer.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#F5F8FF] flex items-center justify-center">
                        <User className="w-5 h-5 text-[#5B6472]" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[#0A0F1E] font-medium truncate">{performer.name}</p>
                      <p className="text-[#5B6472] text-sm truncate">{performer.title || 'Employee'}</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-[#1B5AFF] text-[#1B5AFF]" />
                        <span className="text-[#0A0F1E] font-medium">{performer.avgRating}</span>
                      </div>
                      <p className="text-[#5B6472] text-xs">{performer.reviewCount} reviews</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-[#5B6472]">
                <p>No performer data available</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default PerformanceAnalytics;
